import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import OperatorLayout from "./OperatorLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Search, Globe, FileText, Zap, Check, X, Eye, Plus, Layers } from "lucide-react";
import { cn } from "@/lib/utils";
import BlogEditor from "@/components/operator/BlogEditor";

type ClusterType = "trade_city" | "trade_price" | "trade_emergency" | "trade_comparison" | "trade_funding" | "trade_tips";

const CLUSTER_OPTIONS: { key: ClusterType; label: string; desc: string }[] = [
  { key: "trade_city", label: "Stadt × Gewerk", desc: "Standard-Landingpages" },
  { key: "trade_price", label: "Kosten & Preise", desc: "Preis-Ratgeber" },
  { key: "trade_emergency", label: "Notdienst", desc: "24/7-Notfall-Seiten" },
  { key: "trade_comparison", label: "Vergleich", desc: "Anbieter-Vergleich" },
  { key: "trade_funding", label: "Förderung", desc: "Zuschüsse & Förderungen" },
  { key: "trade_tips", label: "Tipps & Ratgeber", desc: "Praxis-Ratgeber" },
];

export default function OperatorSEO() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<"pages" | "cities" | "trades" | "blog">("pages");
  const [pages, setPages] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [trades, setTrades] = useState<any[]>([]);
  const [blogs, setBlogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [localeFilter, setLocaleFilter] = useState("de");
  const [generating, setGenerating] = useState(false);
  const [editingBlogId, setEditingBlogId] = useState<string | null | undefined>(undefined);
  const [clusterType, setClusterType] = useState<ClusterType>("trade_city");

  const locales = ["de", "at", "ch", "nl", "be-nl", "be-fr", "fr", "pl"];

  useEffect(() => { loadData(); }, [tab, localeFilter]);

  const loadData = async () => {
    setLoading(true);
    if (tab === "pages") {
      const { data } = await supabase
        .from("seo_landing_pages")
        .select("*, seo_trades(trade_label), seo_cities(city_name)")
        .eq("locale_prefix", localeFilter)
        .order("created_at", { ascending: false })
        .limit(200);
      setPages(data || []);
    } else if (tab === "cities") {
      const { data } = await supabase
        .from("seo_cities")
        .select("*")
        .eq("locale_prefix", localeFilter)
        .order("sort_order")
        .limit(200);
      setCities(data || []);
    } else if (tab === "trades") {
      const { data } = await supabase
        .from("seo_trades")
        .select("*")
        .eq("locale_prefix", localeFilter)
        .order("sort_order")
        .limit(100);
      setTrades(data || []);
    } else if (tab === "blog") {
      const { data } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("locale_prefix", localeFilter)
        .order("created_at", { ascending: false })
        .limit(100);
      setBlogs(data || []);
    }
    setLoading(false);
  };

  const generatePages = async (cluster?: ClusterType) => {
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("seo-generate", {
        body: { locale_prefix: localeFilter, action: "generate_all", cluster_type: cluster || clusterType },
      });
      if (error) throw error;
      const clusterLabel = CLUSTER_OPTIONS.find(c => c.key === (cluster || clusterType))?.label || "";
      toast.success(`${data?.generated || 0} ${clusterLabel}-Seiten generiert (${data?.total_remaining || 0} verbleibend)`);
      loadData();
    } catch (err: any) {
      toast.error(err.message || "Fehler bei der Generierung");
    } finally {
      setGenerating(false);
    }
  };

  const togglePublish = async (id: string, current: boolean) => {
    await supabase.from("seo_landing_pages").update({ is_published: !current, published_at: !current ? new Date().toISOString() : null }).eq("id", id);
    loadData();
  };

  const toggleBlogPublish = async (id: string, current: boolean) => {
    await supabase.from("blog_posts").update({ is_published: !current, published_at: !current ? new Date().toISOString() : null }).eq("id", id);
    loadData();
  };

  const tabs = [
    { key: "pages" as const, label: "Landingpages", icon: FileText },
    { key: "cities" as const, label: "Städte", icon: Globe },
    { key: "trades" as const, label: "Gewerke", icon: Zap },
    { key: "blog" as const, label: "Blog", icon: FileText },
  ];

  const filteredPages = pages.filter((p) => !search || p.slug.includes(search.toLowerCase()) || p.h1?.toLowerCase().includes(search.toLowerCase()));

  // Cluster-type slug pattern detection
  const getClusterBadge = (slug: string) => {
    if (slug.includes("-kosten-")) return { label: "Kosten", color: "text-blue-600 bg-blue-50" };
    if (slug.includes("-notdienst-")) return { label: "Notdienst", color: "text-red-600 bg-red-50" };
    if (slug.includes("-vergleich-")) return { label: "Vergleich", color: "text-purple-600 bg-purple-50" };
    if (slug.includes("-foerderung-")) return { label: "Förderung", color: "text-green-600 bg-green-50" };
    if (slug.includes("-tipps-")) return { label: "Tipps", color: "text-amber-600 bg-amber-50" };
    return { label: "Standard", color: "text-muted-foreground bg-secondary/50" };
  };

  // Blog editor view
  if (editingBlogId !== undefined) {
    return (
      <OperatorLayout title="SEO Engine" subtitle="Blog-Beitrag bearbeiten">
        <BlogEditor
          postId={editingBlogId}
          localePrefix={localeFilter}
          onClose={() => setEditingBlogId(undefined)}
          onSaved={() => { setEditingBlogId(undefined); loadData(); }}
        />
      </OperatorLayout>
    );
  }

  return (
    <OperatorLayout title="SEO Engine" subtitle="Landingpages, Städte, Gewerke & Blog verwalten">
      {/* Tab bar */}
      <div className="flex gap-1 mb-6 border-b border-border">
        {tabs.map((tb) => (
          <button
            key={tb.key}
            onClick={() => setTab(tb.key)}
            className={cn(
              "flex items-center gap-1.5 px-4 py-2.5 text-sm transition-colors border-b-2 -mb-px",
              tab === tb.key ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            <tb.icon className="w-4 h-4" />
            {tb.label}
          </button>
        ))}
      </div>

      {/* Locale filter + actions */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="flex gap-1 bg-secondary/30 rounded-sm p-0.5">
          {locales.map((l) => (
            <button
              key={l}
              onClick={() => setLocaleFilter(l)}
              className={cn("px-3 py-1.5 text-xs rounded-sm transition-colors", localeFilter === l ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>
        {tab === "pages" && (
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Suchen..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>
        )}
        {tab === "blog" && (
          <div className="ml-auto">
            <Button variant="commit" size="sm" onClick={() => setEditingBlogId(null)}>
              <Plus className="w-4 h-4 mr-1" /> Neuer Beitrag
            </Button>
          </div>
        )}
      </div>

      {/* Cluster type selector for pages tab */}
      {tab === "pages" && (
        <div className="mb-6 p-4 border border-border bg-card rounded-sm">
          <div className="flex items-center gap-2 mb-3">
            <Layers className="w-4 h-4 text-accent" />
            <span className="text-sm font-medium">Cluster-Typ für Generierung</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
            {CLUSTER_OPTIONS.map((c) => (
              <button
                key={c.key}
                onClick={() => setClusterType(c.key)}
                className={cn(
                  "p-3 text-left border rounded-sm transition-colors text-xs",
                  clusterType === c.key
                    ? "border-primary bg-primary/5 text-foreground"
                    : "border-border text-muted-foreground hover:border-primary/30"
                )}
              >
                <span className="font-medium block">{c.label}</span>
                <span className="text-[10px] opacity-70">{c.desc}</span>
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="commit" size="sm" onClick={() => generatePages()} disabled={generating}>
              {generating ? "Generiert..." : `${CLUSTER_OPTIONS.find(c => c.key === clusterType)?.label} generieren (AI)`}
            </Button>
            <Button variant="quiet" size="sm" onClick={() => {
              // Generate all cluster types sequentially
              const allTypes: ClusterType[] = ["trade_city", "trade_price", "trade_emergency", "trade_comparison", "trade_funding", "trade_tips"];
              let idx = 0;
              const genNext = async () => {
                if (idx >= allTypes.length) return;
                setClusterType(allTypes[idx]);
                await generatePages(allTypes[idx]);
                idx++;
                genNext();
              };
              genNext();
            }} disabled={generating}>
              Alle Cluster generieren
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-caption py-8 text-center">{t("common.loading")}</p>
      ) : tab === "pages" ? (
        <div className="border border-border rounded-sm overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/30"><tr>
              <th className="p-3 text-left font-medium">Slug</th>
              <th className="p-3 text-left font-medium">Typ</th>
              <th className="p-3 text-left font-medium">Gewerk</th>
              <th className="p-3 text-left font-medium">Stadt</th>
              <th className="p-3 text-left font-medium">Wörter</th>
              <th className="p-3 text-left font-medium">Status</th>
              <th className="p-3 text-left font-medium">Aktion</th>
            </tr></thead>
            <tbody>
              {filteredPages.map((p) => {
                const badge = getClusterBadge(p.slug);
                return (
                <tr key={p.id} className="border-t border-border">
                  <td className="p-3 font-mono text-xs">{p.slug}</td>
                  <td className="p-3">
                    <span className={cn("text-[10px] px-1.5 py-0.5 rounded font-medium", badge.color)}>{badge.label}</span>
                  </td>
                  <td className="p-3">{p.seo_trades?.trade_label || "-"}</td>
                  <td className="p-3">{p.seo_cities?.city_name || "-"}</td>
                  <td className="p-3">{p.word_count}</td>
                  <td className="p-3">
                    {p.is_published ? (
                      <span className="inline-flex items-center gap-1 text-xs text-primary"><Check className="w-3 h-3" />Live</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><X className="w-3 h-3" />Entwurf</span>
                    )}
                  </td>
                  <td className="p-3 flex gap-2">
                    <Button variant="quiet" size="sm" onClick={() => togglePublish(p.id, p.is_published)}>
                      {p.is_published ? "Depublish" : "Publish"}
                    </Button>
                    <Button variant="quiet" size="sm" onClick={() => window.open(`/${localeFilter}/${p.slug}`, "_blank")}>
                      <Eye className="w-3 h-3" />
                    </Button>
                  </td>
                </tr>
                );
              })}
              {filteredPages.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Keine Seiten gefunden. Wähle einen Cluster-Typ und klicke "Generieren".</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ) : tab === "cities" ? (
        <div className="border border-border rounded-sm overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/30"><tr>
              <th className="p-3 text-left font-medium">Stadt</th>
              <th className="p-3 text-left font-medium">Slug</th>
              <th className="p-3 text-left font-medium">Region</th>
              <th className="p-3 text-left font-medium">Aktiv</th>
            </tr></thead>
            <tbody>
              {cities.map((c) => (
                <tr key={c.id} className="border-t border-border">
                  <td className="p-3 font-medium">{c.city_name}</td>
                  <td className="p-3 font-mono text-xs">{c.city_slug}</td>
                  <td className="p-3">{c.region}</td>
                  <td className="p-3">{c.is_active ? <Check className="w-4 h-4 text-primary" /> : <X className="w-4 h-4 text-muted-foreground" />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : tab === "trades" ? (
        <div className="border border-border rounded-sm overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/30"><tr>
              <th className="p-3 text-left font-medium">Gewerk</th>
              <th className="p-3 text-left font-medium">Slug</th>
              <th className="p-3 text-left font-medium">Key</th>
              <th className="p-3 text-left font-medium">Aktiv</th>
            </tr></thead>
            <tbody>
              {trades.map((tr) => (
                <tr key={tr.id} className="border-t border-border">
                  <td className="p-3 font-medium">{tr.trade_label}</td>
                  <td className="p-3 font-mono text-xs">{tr.trade_slug}</td>
                  <td className="p-3 text-xs">{tr.trade_key}</td>
                  <td className="p-3">{tr.is_active ? <Check className="w-4 h-4 text-primary" /> : <X className="w-4 h-4 text-muted-foreground" />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="border border-border rounded-sm overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/30"><tr>
              <th className="p-3 text-left font-medium">Titel</th>
              <th className="p-3 text-left font-medium">Kategorie</th>
              <th className="p-3 text-left font-medium">Status</th>
              <th className="p-3 text-left font-medium">Aktion</th>
            </tr></thead>
            <tbody>
              {blogs.map((b) => (
                <tr key={b.id} className="border-t border-border">
                  <td className="p-3 font-medium">{b.title}</td>
                  <td className="p-3">{b.category || "-"}</td>
                  <td className="p-3">
                    {b.is_published ? <span className="text-xs text-primary">Live</span> : <span className="text-xs text-muted-foreground">Entwurf</span>}
                  </td>
                  <td className="p-3 flex gap-2">
                    <Button variant="quiet" size="sm" onClick={() => setEditingBlogId(b.id)}>
                      Bearbeiten
                    </Button>
                    <Button variant="quiet" size="sm" onClick={() => toggleBlogPublish(b.id, b.is_published)}>
                      {b.is_published ? "Depublish" : "Publish"}
                    </Button>
                  </td>
                </tr>
              ))}
              {blogs.length === 0 && (
                <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">Keine Blog-Beiträge vorhanden</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </OperatorLayout>
  );
}
